// API helpers for Agent Studio (NM Agent v2 — backend: AgentV2Controller).
// Uses the same Bearer token as the rest of the app.

const authHeaders = () => ({
  Accept: "application/json",
  "Content-Type": "application/json",
  Authorization: "Bearer " + window.localStorage.getItem("laravel-token"),
});

export class AgentApiError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.status = status;
    this.data = data; // the run, when the API sends it back with an error (e.g. a failed launch)
  }
}

const request = async (baseUrl, path, options = {}) => {
  if (!baseUrl) throw new AgentApiError("Backend URL is not loaded yet.", 0);
  let res;
  try {
    const headers = authHeaders();
    // File uploads: let the browser set the multipart boundary.
    if (typeof FormData !== "undefined" && options.body instanceof FormData) delete headers["Content-Type"];
    res = await fetch(baseUrl + path, { mode: "cors", headers, ...options });
  } catch (e) {
    throw new AgentApiError("Network error — is the API reachable?", 0);
  }
  let body = null;
  try {
    body = await res.json();
  } catch (e) {
    body = null;
  }
  if (!res.ok) {
    const msg =
      body?.message ||
      (res.status === 401 ? "Your session expired. Please log in again." : `Request failed (${res.status}).`);
    throw new AgentApiError(msg, res.status, body?.data || null);
  }
  return body;
};

export const agentApi = (baseUrl) => ({
  // scope: "visible" (mine + shared with the team; admins: everyone's), "mine" or "shared".
  // Returns { data, viewer: { id, name, is_admin } }.
  // Recent runs, a page at a time (newest first): meta = {page, per_page, total, pages, latest_id}.
  listRuns: (limit = 50, scope = "visible", page = 1) =>
    request(baseUrl, `agent/v2/runs?limit=${limit}&scope=${encodeURIComponent(scope)}&page=${page}`).then((b) => ({
      data: b?.data || [],
      viewer: b?.viewer || null,
      meta: b?.meta || null,
    })),
  getRun: (id) => request(baseUrl, `agent/v2/runs/${id}`).then((b) => b?.data || null),
  // Today's ideas → Trends, "What's moving?" (reporting database). Topic may be empty (= everything).
  // Trends → the keywords behind one ad set's move (same periods as the trends list).
  trendKeywords: ({ adset_id, mode = "custom", cur_from, cur_to, base_from, base_to }) =>
    request(
      baseUrl,
      `agent/v2/trends/keywords?${new URLSearchParams(Object.entries({ adset_id, mode, cur_from, cur_to, base_from, base_to }).filter(([, v]) => v !== undefined && v !== "")).toString()}`
    ).then((b) => b?.data),
  trends: ({ mode = "yesterday_vs_7d", topic = "", exclude = "", cur_from, cur_to, base_from, base_to }) =>
    request(
      baseUrl,
      `agent/v2/trends?${new URLSearchParams(Object.entries({ mode, topic, exclude, cur_from, cur_to, base_from, base_to }).filter(([, v]) => v !== undefined && v !== "")).toString()}`
    ).then((b) => b?.data || null),
  // Today's ideas → our trending articles (reporting database).
  trendingArticles: ({ q = "", exclude = "", days = 90, sort = "gp" }) =>
    request(baseUrl, `agent/v2/trending-articles?q=${encodeURIComponent(q)}&exclude=${encodeURIComponent(exclude)}&days=${days}&sort=${encodeURIComponent(sort)}`).then((b) => b?.data || null),
  // Today's ideas → Money moves (spend report, reporting database).
  nextMoves: ({ buyer = "" } = {}) => request(baseUrl, `agent/v2/next-moves?buyer=${encodeURIComponent(buyer)}`).then((b) => b?.data || null),
  hotTopics: () => request(baseUrl, "agent/v2/hot-topics").then((b) => b?.data || null),
  hotTopicResearch: ({ topic, category = "" }) =>
    request(baseUrl, "agent/v2/hot-topics/research", { method: "POST", body: JSON.stringify({ topic, category }) }).then((b) => b?.data || null),
  dailyUpdate: () => request(baseUrl, "agent/v2/daily-update").then((b) => (b ? b.data ?? null : null)),
  moneyMoves: ({ buyer = "" } = {}) => request(baseUrl, `agent/v2/money-moves?buyer=${encodeURIComponent(buyer)}`).then((b) => b?.data || null),
  // Today's ideas → Adset: what one ad set spent, earned and served (keyword_intelligence.adset_lookup).
  adsetLookup: ({ q, days = 365 }) => request(baseUrl, `agent/v2/adset-lookup?q=${encodeURIComponent(q)}&days=${days}`).then((b) => b?.data || null),
  // Today's ideas → Topic & keywords (the team's keyword_finder functions, reporting database).
  topicFinder: ({ q, exclude = "", days = 365 }) =>
    request(baseUrl, `agent/v2/topic-finder?q=${encodeURIComponent(q)}&exclude=${encodeURIComponent(exclude)}&days=${days}`).then((b) => b?.data || null),
  // "What do you want to run?" — our best keywords for ad sets named "(q)" (reporting database).
  topKeywords: ({ q, days = 30, minClicks = 200 }) =>
    request(baseUrl, `agent/v2/top-keywords?q=${encodeURIComponent(q)}&days=${days}&min_clicks=${minClicks}`).then((b) => b?.data || null),
  startRun: (payload) =>
    request(baseUrl, "agent/v2/runs", { method: "POST", body: JSON.stringify(payload) }),
  // Stop a queued or running plan (what is done so far is kept; Retry continues).
  stopRun: (id) => request(baseUrl, `agent/v2/runs/${id}/stop`, { method: "POST", body: "{}" }).then((b) => b?.data),
  retryRun: (id) => request(baseUrl, `agent/v2/runs/${id}/retry`, { method: "POST", body: "{}" }).then((b) => b?.data),
  research: (payload) => request(baseUrl, "agent/v2/research", { method: "POST", body: JSON.stringify(payload) }).then((b) => b?.data),
  listResearch: (limit = 10, scope = "visible") =>
    request(baseUrl, `agent/v2/research?limit=${limit}&scope=${encodeURIComponent(scope)}`).then((b) => b?.data || []),
  shareRun: (id, shared) => request(baseUrl, `agent/v2/runs/${id}/share`, { method: "POST", body: JSON.stringify({ shared }) }).then((b) => b?.data),
  shareResearch: (id, shared) => request(baseUrl, `agent/v2/research/${id}/share`, { method: "POST", body: JSON.stringify({ shared }) }).then((b) => b?.data),
  // Review (owner or admin). revision = plan.revision the person was looking at.
  savePlan: (id, revision, edits) =>
    request(baseUrl, `agent/v2/runs/${id}/plan`, { method: "POST", body: JSON.stringify({ revision, ...edits }) }).then((b) => b?.data),
  uploadImage: (id, index, file, mode, revision) => {
    const fd = new FormData();
    fd.append("image", file);
    fd.append("mode", mode);
    fd.append("revision", String(revision));
    return request(baseUrl, `agent/v2/runs/${id}/ads/${index}/image`, { method: "POST", body: fd }).then((b) => b?.data);
  },
  redrawImage: (id, index, template, copy, revision) =>
    request(baseUrl, `agent/v2/runs/${id}/ads/${index}/redraw`, { method: "POST", body: JSON.stringify({ template, copy, revision }) }).then((b) => b?.data),
  // "Create a variation": put a winning reference ad's own image on this ad (no AI, instant).
  useWinnerImage: (id, index, adId, revision) =>
    request(baseUrl, `agent/v2/runs/${id}/ads/${index}/winner`, { method: "POST", body: JSON.stringify({ ad_id: adId, revision }) }).then((b) => b?.data),
  addAd: (id, revision, from, duplicate = false) =>
    request(baseUrl, `agent/v2/runs/${id}/ads/add`, { method: "POST", body: JSON.stringify({ revision, ...(from ? { from } : {}), duplicate: !!duplicate }) }).then((b) => b?.data),
  // Edit plan: every dropped image becomes a new ad with the plan's copy.
  uploadAds: (id, files, mode, revision) => {
    const fd = new FormData();
    files.forEach((f) => fd.append("images[]", f));
    fd.append("mode", mode || "as_is");
    fd.append("revision", String(revision));
    return request(baseUrl, `agent/v2/runs/${id}/ads/upload`, { method: "POST", body: fd }).then((b) => b?.data);
  },
  // Edit plan "Create images" for the ads without an image: {source: custom|reference|keywords, prompt, mode, count}.
  createAdImages: (id, payload, revision) =>
    request(baseUrl, `agent/v2/runs/${id}/ads/images`, { method: "POST", body: JSON.stringify({ ...payload, revision }) }).then((b) => b?.data),
  removeAd: (id, index, revision) => request(baseUrl, `agent/v2/runs/${id}/ads/${index}/remove`, { method: "POST", body: JSON.stringify({ revision }) }).then((b) => b?.data),
  // What keywords earned for us (revenue, clicks, RPC, est. margin) — Edit plan, under the feed terms.
  keywordStats: (keywords) =>
    request(baseUrl, `agent/v2/keyword-stats?${keywords.map((k) => `keywords[]=${encodeURIComponent(k)}`).join("&")}`).then((b) => b?.data || []),
  likeReferenceImage: (id, index, revision) =>
    request(baseUrl, `agent/v2/runs/${id}/ads/${index}/like-reference`, { method: "POST", body: JSON.stringify({ revision }) }).then((b) => b?.data),
  restoreImage: (id, index, revision) =>
    request(baseUrl, `agent/v2/runs/${id}/ads/${index}/restore`, { method: "POST", body: JSON.stringify({ revision }) }).then((b) => b?.data),
  // Choices for Edit plan: businesses, ad managers, pages, redirect domains, default subdomain.
  planOptions: (businessId) =>
    request(baseUrl, `agent/v2/plan-options${businessId ? `?business_id=${encodeURIComponent(businessId)}` : ""}`).then((b) => b?.data || null),
  // Serp Pixel ID list of an ad account ({data: [{id, name}], default}).
  pixels: (businessId, adAccountId) =>
    request(baseUrl, `agent/v2/pixels?business_id=${encodeURIComponent(businessId || "")}&ad_account_id=${encodeURIComponent(adAccountId || "")}`).then((b) => ({
      list: b?.data || [],
      def: b?.default || "",
    })),
  // Site / author suggestions (same endpoints as Quick Blog & FB Campaign Launch).
  suggestSites: (prompt) =>
    request(baseUrl, "suggest-site-from-prompt", { method: "POST", body: JSON.stringify({ prompt: prompt || "" }) }).then((b) => (Array.isArray(b?.data) ? b.data : [])),
  suggestAuthors: (prompt, site) =>
    request(baseUrl, "suggest-author-from-prompt", { method: "POST", body: JSON.stringify({ prompt: prompt || "", site: site || "" }) }).then((b) =>
      Array.isArray(b?.data) ? b.data : []
    ),
  // Blog featured image from the article title (like Create Blog Post).
  // "Recommended from our data": ad manager & page, redirect domain, article site, RAC, keywords — each with its edit.
  recommendations: (id) => request(baseUrl, `agent/v2/runs/${id}/recommendations`).then((b) => b?.data || []),
  // The launched Facebook campaign: read its status (from Facebook) or switch it on / off.
  campaignStatus: (id) => request(baseUrl, `agent/v2/runs/${id}/campaign-status`).then((b) => b?.data),
  setCampaignStatus: (id, status) =>
    request(baseUrl, `agent/v2/runs/${id}/campaign-status`, { method: "POST", body: JSON.stringify({ status }) }).then((b) => b?.data),
  // Write the article again for the plan's current feed terms (1-3 minutes; the featured image is kept).
  rewriteArticle: (id, revision) => request(baseUrl, `agent/v2/runs/${id}/article`, { method: "POST", body: JSON.stringify({ revision }) }).then((b) => b?.data),
  featuredImage: (id, revision) => request(baseUrl, `agent/v2/runs/${id}/featured-image`, { method: "POST", body: JSON.stringify({ revision }) }).then((b) => b?.data),
  // Existing blog terms (same endpoint as the launch screens' term search).
  searchTerms: (q) =>
    request(baseUrl, `blog-terms-listing${q ? `?search=${encodeURIComponent(q)}` : ""}`).then((b) =>
      (b?.data || []).map((t) => ({ value: t.id, label: t.name || t.id, terms: Array.isArray(t.terms) ? t.terms : [] }))
    ),
  // What the agent learned from edits (yours + team's from shared plans; admins: all).
  listLearnings: (scope = "visible") => request(baseUrl, `agent/v2/learnings?scope=${encodeURIComponent(scope)}`).then((b) => b?.data || []),
  setLearning: (id, enabled) => request(baseUrl, `agent/v2/learnings/${id}`, { method: "POST", body: JSON.stringify({ enabled }) }).then((b) => b?.data),
  deleteLearning: (id) => request(baseUrl, `agent/v2/learnings/${id}`, { method: "DELETE" }).then((b) => b?.data),
  // Past prompts similar to what is being typed.
  promptSuggestions: (q) => request(baseUrl, `agent/v2/prompt-suggestions?q=${encodeURIComponent(q)}&recent=1`).then((b) => b?.data || []),
  // "Plan brief": what the plan will use from the prompt ({rows, keywords, needs_attention, standard, changed}). No AI, nothing saved.
  // Launch Desk (first screen): where to launch today, winners to clone, topics not to launch (cached for the day).
  // period: "1d" | "3d" (default) | "7d" | "30d" | "custom" (with from / to, YYYY-MM-DD).
  launchDesk: (refresh = false, period = "3d", from = "", to = "") => {
    const q = new URLSearchParams({ period: period || "3d" });
    if (period === "custom") {
      q.set("from", from || "");
      q.set("to", to || "");
    }
    if (refresh) q.set("refresh", "1");
    return request(baseUrl, `agent/v2/launch-desk?${q.toString()}`).then((b) => b?.data || null);
  },
  // "Plan this clone": what to keep and change for a winning ad set, and the prompt for New plan.
  cloneBrief: (adset) => request(baseUrl, "agent/v2/clone-brief", { method: "POST", body: JSON.stringify({ adset }) }).then((b) => b?.data || null),
  // The 20% check before building: Go / Caution / Stop, the expected margin and why.
  marginCheck: (prompt, variationOf) =>
    request(baseUrl, "agent/v2/margin-check", { method: "POST", body: JSON.stringify({ prompt: prompt || "", variation_of: variationOf || "" }) }).then((b) => b?.data || null),
  // "Recommended setup": the best ad account & page, site, redirect, audience, pixel and term set for the plan's category.
  setupAdvice: (prompt, variationOf) =>
    request(baseUrl, "agent/v2/setup-advice", { method: "POST", body: JSON.stringify({ prompt: prompt || "", variation_of: variationOf || "" }) }).then((b) => b?.data || null),
  promptBrief: (prompt, variationOf) =>
    request(baseUrl, "agent/v2/prompt-brief", { method: "POST", body: JSON.stringify({ prompt: prompt || "", variation_of: variationOf || "" }) }).then(
      (b) => b?.data || null
    ),
  // Custom ad image descriptions used before (for the New plan form's "Custom image").
  imagePromptSuggestions: (q, runId) =>
    request(baseUrl, `agent/v2/image-prompt-suggestions?q=${encodeURIComponent(q || "")}${runId ? `&run_id=${runId}` : ""}`).then((b) => b?.data || []),
  approveRun: (id, revision) => request(baseUrl, `agent/v2/runs/${id}/approve`, { method: "POST", body: JSON.stringify({ revision }) }).then((b) => b?.data),
  reopenRun: (id) => request(baseUrl, `agent/v2/runs/${id}/reopen`, { method: "POST", body: "{}" }).then((b) => b?.data),
  // Launch: { enabled, problems[], resume, summary, steps } — nothing is created by this call.
  launchCheck: (id) => request(baseUrl, `agent/v2/runs/${id}/launch-check`).then((b) => b?.data || null),
  // Creates the blog term + post, INT RSOC row and the FB campaign (PAUSED) / ad set / ads (active).
  launchRun: (id, revision, publishBlog) =>
    request(baseUrl, `agent/v2/runs/${id}/launch`, { method: "POST", body: JSON.stringify({ revision, publish_blog: publishBlog }) }).then((b) => b?.data),
  // Top bar bell: my finished plans (ready / needs fixes / failed), unread count, and whether one is building.
  notifications: () => request(baseUrl, "agent/v2/notifications").then((b) => b?.data),
  notificationsSeen: (ids) =>
    request(baseUrl, "agent/v2/notifications/seen", { method: "POST", body: JSON.stringify(ids ? { ids } : {}) }),
  rejectRun: (id, reason) =>
    request(baseUrl, `agent/v2/runs/${id}/reject`, { method: "POST", body: JSON.stringify({ reason }) }).then((b) => b?.data),
});
